import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Avatar,
  Chip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAuth } from "../context/AuthContext";
import Logo from "../assets/png.png";
import { useNavigate } from "react-router-dom";
import { brand } from "../theme";

const roleColors = {
  super_admin: { bg: brand.grayLight, color: brand.black, label: "Super Admin" },
  agency_owner: { bg: brand.terracottaTint, color: brand.terracotta, label: "Agency Owner" },
  agency_staff: { bg: "#dcfce7", color: "#15803d", label: "Agency Staff" },
  sales: { bg: "#FBF3D9", color: brand.goldDark, label: "Sales" },
  helpdesk: { bg: "#fee2e2", color: "#b91c1c", label: "Helpdesk" },
  accounts: { bg: "#FBF3D9", color: brand.goldDark, label: "Accounts" },
};

const Topbar = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleProfile = () => {
    navigate("/profile");
    handleClose();
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    handleClose();
  };

  const userInitial = (
    user?.name?.charAt(0) ||
    user?.email?.charAt(0) ||
    "?"
  ).toUpperCase();
  const displayName = user?.name || user?.email || "";
  const roleInfo = roleColors[user?.role];

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{ bgcolor: "white", color: "#0f172a" }}
    >
      <Toolbar
        sx={{ display: "flex", justifyContent: "space-between", minHeight: 64 }}
      >
        {/* Left */}
        <Box display="flex" alignItems="center" gap={1.5}>
          <IconButton
            color="inherit"
            edge="start"
            sx={{
              display: { xs: "inline-flex", lg: "none" },
              color: "#64748b",
            }}
            onClick={onMenuClick}
          >
            <MenuIcon />
          </IconButton>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={Logo}
                alt="Client Portal"
                style={{
                  width: "50%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* Right */}
        <Box display="flex" alignItems="center" gap={1.5}>
          {roleInfo && (
            <Chip
              label={roleInfo.label}
              size="small"
              sx={{
                bgcolor: roleInfo.bg,
                color: roleInfo.color,
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 24,
                display: { xs: "none", sm: "flex" },
              }}
            />
          )}

          <Box
            onClick={handleMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              borderRadius: "10px",
              px: 1,
              py: 0.5,
              border: "1px solid #e2e8f0",
              transition: "all 0.15s ease",
              "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                fontSize: "0.8rem",
                bgcolor: brand.terracotta,
                fontWeight: 700,
              }}
            >
              {userInitial}
            </Avatar>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#0f172a",
                display: { xs: "none", sm: "block" },
                maxWidth: 140,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: "1rem",
                color: "#94a3b8",
                display: { xs: "none", sm: "block" },
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 0.5 }}
          >
            <Box sx={{ px: 2, py: 1.5, minWidth: 200 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#0f172a" }}
              >
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem onClick={handleProfile} sx={{ mt: 0.5 }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: "#64748b" }} />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={500}>
                Profile
              </Typography>
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                color: "#dc2626",
                "& .MuiListItemIcon-root": { color: "#dc2626" },
              }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2" fontWeight={500}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Topbar;
